import { useCallback, useEffect } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { atom, useAtom, useAtomValue, useStore } from "jotai";
import { userIdAtom } from "@/atoms/authAtoms";
import { errorStatus, getCase, getMe, listCases } from "./api";
import type { CaseState, NotifyCase } from "./types";

/** Query keys, each led by the signed-in user (as devicePanelKeys): what one
 * user was served is never served to the next on the same tab. */
export const notifyKeys = {
  me: (user: string | null) => ["notify-me", user] as const,
  cases: (user: string | null, siteId: string, state: CaseState) =>
    ["notify-cases", user, siteId, { state }] as const,
  case: (user: string | null, siteId: string, studyUid: string) =>
    ["notify-case", user, siteId, studyUid] as const,
};

/** Cases are live: unlike the app's default, refetch when the app comes back to
 * the front (a tap on a push) and every 30 s while it is visible. */
const LIVE = {
  staleTime: 0,
  refetchOnWindowFocus: true,
  refetchInterval: 30_000,
} as const;

export function useNotifyMe() {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    queryKey: notifyKeys.me(user),
    queryFn: getMe,
    staleTime: 5 * 60 * 1000,
  });
}

/** The `/me` answer a 403 last refetched it for (serialized). */
const meRefetchedForAtom = atom<string | null>(null);

/**
 * A handler for Notify call errors: on a 403, refetch `/me`, since the caller's
 * roles may have changed while the app was open (it is cached for 5 min) and
 * the pages decide what to show from it. Once per `/me` answer: when the
 * refetch brings the same sites and roles, later 403s don't refetch again, so
 * a 403 that stays (e.g. the 30 s list refetch) never loops.
 */
export function useRefetchMeOnForbidden() {
  const queryClient = useQueryClient();
  const store = useStore();
  const user = useAtomValue(userIdAtom);
  return useCallback(
    (error: unknown) => {
      if (errorStatus(error) !== 403) return;
      const key = notifyKeys.me(user);
      const answer = JSON.stringify(queryClient.getQueryData(key) ?? null);
      if (store.get(meRefetchedForAtom) === answer) return;
      store.set(meRefetchedForAtom, answer);
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
    },
    [queryClient, store, user],
  );
}

/** Cases this tab saw acknowledged by an ack call (the caller's, or from a 409
 * someone else's), by `site_id/study_uid`, for the signed-in `user` only. The
 * list reads a GSI that is eventually consistent, so for a while a refetch can
 * still list a case as open or alerted after its ack; these keep the
 * acknowledged row in view until the server catches up (`supersedesAck`). */
export const ackedCasesAtom = atom<{ user: string | null; cases: Record<string, NotifyCase> }>({
  user: null,
  cases: {},
});

export const caseRef = (row: Pick<NotifyCase, "site_id" | "study_uid">) =>
  `${row.site_id}/${row.study_uid}`;

const STATE_RANK: Record<CaseState, number> = { open: 0, alerted: 1, acknowledged: 2 };

/** How far along a case state is. A state this app doesn't know yet (e.g. S2
 * `closed`) ranks after every known one. */
export const stateRank = (state: string): number => STATE_RANK[state as CaseState] ?? Infinity;

/** A server row that already carries the ack, or a later state: the local copy
 * must not hide it. */
export const supersedesAck = (row: NotifyCase) =>
  !!row.owner_person_id || stateRank(row.state) >= STATE_RANK.acknowledged;

/** One row per case, the furthest-along state winning: a case moving between
 * states can show in two lists for a moment, and a local ack outranks the
 * index's stale open or alerted row. */
export function oneRowPerCase(rows: NotifyCase[]): NotifyCase[] {
  const byRef = new Map<string, NotifyCase>();
  for (const row of rows) {
    const seen = byRef.get(caseRef(row));
    if (!seen || stateRank(row.state) > stateRank(seen.state)) byRef.set(caseRef(row), row);
  }
  return [...byRef.values()];
}

const NO_CASES: Record<string, NotifyCase> = {};

/** This user's locally acknowledged cases, and how to add or drop one. Another
 * user's rows (same tab, after a sign-out) are never returned, and the next
 * `remember` replaces them. */
export function useAckedCases() {
  const user = useAtomValue(userIdAtom);
  const [stored, setStored] = useAtom(ackedCasesAtom);
  const cases = stored.user === user ? stored.cases : NO_CASES;
  const remember = useCallback(
    (row: NotifyCase) =>
      setStored((current) => ({
        user,
        cases: { ...(current.user === user ? current.cases : {}), [caseRef(row)]: row },
      })),
    [setStored, user],
  );
  const forget = useCallback(
    (refs: string[]) =>
      setStored((current) => {
        if (current.user !== user || !refs.some((ref) => ref in current.cases)) return current;
        const cases = { ...current.cases };
        for (const ref of refs) delete cases[ref];
        return { user, cases };
      }),
    [setStored, user],
  );
  return { cases, remember, forget };
}

/** The site's cases in `states`, oldest flag first, one row per case. */
export function useCaseList(siteId: string, states: CaseState[]) {
  const user = useAtomValue(userIdAtom);
  const acked = useAckedCases();
  const results = useQueries({
    queries: states.map((state) => ({
      queryKey: notifyKeys.cases(user, siteId, state),
      queryFn: () => listCases(siteId, state),
      ...LIVE,
    })),
  });
  const served = results.flatMap((result) => result.data ?? []);
  // Local acks the server has caught up with: dropped, never shown over its row.
  const caughtUp = served
    .filter((row) => caseRef(row) in acked.cases && supersedesAck(row))
    .map(caseRef);
  const caughtUpKey = caughtUp.join("\n");
  const { forget } = acked;
  useEffect(() => {
    if (caughtUpKey) forget(caughtUpKey.split("\n"));
  }, [caughtUpKey, forget]);
  const local = Object.values(acked.cases).filter(
    (row) => row.site_id === siteId && !caughtUp.includes(caseRef(row)),
  );
  const cases = oneRowPerCase([...served, ...local])
    .filter((row) => states.includes(row.state))
    .sort((a, b) => a.flagged_at.localeCompare(b.flagged_at));
  return {
    cases,
    isLoading: results.some((result) => result.isLoading),
    error: results.find((result) => result.error)?.error ?? null,
  };
}

/** One case and its events. */
export function useCaseDetail(siteId: string, studyUid: string, enabled: boolean) {
  const user = useAtomValue(userIdAtom);
  return useQuery({
    queryKey: notifyKeys.case(user, siteId, studyUid),
    queryFn: () => getCase(siteId, studyUid),
    enabled,
    ...LIVE,
  });
}
